import { useEffect, useState } from 'react';

export type NotificationPermission = 'default' | 'granted' | 'denied';

interface UseNotificationsReturn {
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (title: string, options?: NotificationOptions) => Promise<void>;
  isSupported: boolean;
}

/**
 * Manages browser notification permissions and display.
 * Useful for showing system notifications for important events.
 * 
 * @returns Object with permission state and notification functions
 */
export function useNotifications(): UseNotificationsReturn {
  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission as NotificationPermission;
  });

  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  useEffect(() => {
    if (!isSupported) {
      return;
    }

    // Update permission state if it changes
    const checkPermission = () => {
      setPermission(Notification.permission as NotificationPermission);
    };

    // Check permission periodically (some browsers don't fire events)
    const interval = setInterval(checkPermission, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isSupported]);

  /**
   * Request notification permission from the user
   */
  const requestPermission = async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      console.warn('Notifications are not supported in this browser');
      return 'denied';
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result as NotificationPermission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  };

  /**
   * Show a browser notification
   * @param title - Notification title
   * @param options - Notification options (body, icon, etc.)
   */
  const showNotification = async (title: string, options?: NotificationOptions) => {
    if (!isSupported) {
      console.warn('Notifications are not supported in this browser');
      return;
    }

    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(title, {
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          ...options,
        });
        return;
      }

      throw new Error('Service worker not available');
    } catch (error) {
      console.error('Error showing notification:', error);
      try {
        const notification = new Notification(title, {
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          ...options,
        });
        setTimeout(() => {
          notification.close();
        }, 5000);
      } catch (fallbackError) {
        console.error('Fallback notification failed:', fallbackError);
      }
    }
  };

  return {
    permission,
    requestPermission,
    showNotification,
    isSupported,
  };
}
